import styled from "styled-components";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSessionFocus } from "hooks/useSessionFocus";
import { pillsByName } from "hooks/useSoloAccuracy";
import { useClassroomStudentNames } from "hooks/useClassroomStudentNames";
import { ScorePill } from "components/shared/ScorePill/ScorePill";
import { TagLevelSmall } from "components/shared/TagLevelSmall/TagLevelSmall";
import { parseLevelFromString } from "utils";
import { SOLO_ACCURACY_RULE } from "utils/soloAccuracy";
import info2 from "assets/info2.svg";

// The collapsed report row's scores section: what the session worked on, named
// the way the dashboard group card names it, and each student's solo score.
//
// Ungated on purpose. The solo accuracy on the row is not behind the
// deterministic-report flag — that gate covers only what reaches the LLM prompt
// — so the pill is not behind it either, and no session start date is checked.

const SectionLabel = styled.p`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  max-width: 100%;
`;

// The code and the title are separate elements, but they must not
// run together as text: JSX drops the newline between adjacent expressions, so
// "G8U1L2" + "Naming the Moves" rendered as "G8U1L2Naming the Moves". The
// delimiter is the colon the old "Student Proficiency for 5.NF.B.7c: …" header
// used, so the separation reads the way teachers already expect. It carries its
// own margin so the gap survives even where the colon is not wanted.
const Separator = styled.span`
  margin-right: 4px;
`;

// The code keeps its own element beside the title, exactly as the dashboard
// group card shows it, so the teacher reads one vocabulary across both screens.
const CodeText = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const TitleText = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const CountLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
`;

// Names fill three rows, then the next column, as the live pill's roster does.
// The row count and the column width are explicit, so the wrap point never
// depends on pill width and a long name is cut short instead of widening its
// column: 160px with a score pill, 117px for plain participant names.
const StudentsGrid = styled.div`
  display: grid;
  grid-template-rows: repeat(${({ $rows }) => $rows || 1}, auto);
  grid-auto-flow: column;
  grid-auto-columns: ${({ $columnWidth }) => $columnWidth}px;
  justify-content: start;
  gap: 6px 16px;
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  & > :not(:last-child) {
    flex-shrink: 0;
  }
`;

const StudentName = styled.span`
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PillMeta = styled.p`
  font-size: 14px;
  color: #000;
  margin: 0;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InlineInfoIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-left: 6px;
  cursor: pointer;
  vertical-align: middle;
`;

// Highest score first, ties broken by name — the order teachers already read.
// The score is the solo accuracy where it resolved, and the maths level in the
// fallback. Only the value changes, never the rule.
const byScoreThenName = (scoreOf) => (a, b) => {
  const scoreA = scoreOf(a);
  const scoreB = scoreOf(b);
  const hasA = typeof scoreA === "number" && !Number.isNaN(scoreA);
  const hasB = typeof scoreB === "number" && !Number.isNaN(scoreB);
  if (hasA && !hasB) return -1;
  if (!hasA && hasB) return 1;
  if (!hasA && !hasB) return a.student_name.localeCompare(b.student_name);
  if (scoreB !== scoreA) return scoreB - scoreA;
  return a.student_name.localeCompare(b.student_name);
};

// The helper can fail to name a session; the row then says what it always said,
// which is the standard the session itself recorded.
const fallbackCode = (value) =>
  value?.standard_prefix || value?.standards?.[0]?.standard_code || null;

const ScoredStudents = ({ students, pillFor, showScores }) => {
  const scoreOf = (student) => {
    if (!showScores) {
      return parseLevelFromString(student.math_learning_category) || 0;
    }
    const pill = pillFor(student);
    if (!pill || pill.not_applicable || pill.notApplicable) return null;
    return typeof pill.accuracy === "number" ? pill.accuracy : null;
  };

  return (
    <StudentsGrid
      data-testid="students-grid"
      $rows={Math.min(students.length, 3)}
      $columnWidth={160}
    >
      {[...students].sort(byScoreThenName(scoreOf)).map((student) => {
        const pill = pillFor(student);
        return (
          <StudentRow
            key={student.student_id || student.student_name}
            data-testid="student-row"
          >
            {showScores ? (
              <ScorePill
                correct={pill?.correct}
                answered={pill?.answered}
                accuracy={pill?.accuracy}
                notApplicable={pill?.not_applicable || pill?.notApplicable}
              />
            ) : (
              <TagLevelSmall
                compact
                level={parseLevelFromString(student.math_learning_category)}
              />
            )}
            <StudentName data-testid="student-name" title={student.student_name}>
              {student.student_name}
            </StudentName>
          </StudentRow>
        );
      })}
    </StudentsGrid>
  );
};

const ParticipantNames = ({ names }) => (
  <StudentsGrid data-testid="students-grid" $rows={Math.min(names.length, 3)} $columnWidth={117}>
    {names.map((name) => (
      <StudentRow key={name} data-testid="student-row">
        <StudentName data-testid="student-name" title={name}>
          {name}
        </StudentName>
      </StudentRow>
    ))}
  </StudentsGrid>
);

export const SessionScores = ({ value, educatorId }) => {
  const focus = useSessionFocus(value);
  const nameById = useClassroomStudentNames(value?.classroom_id, educatorId);
  // The scores arrive on the session row itself. They used to be one request
  // per rendered row, and the pills are on the collapsed card, so there was no
  // point at which the frontend could defer them.
  const byName = pillsByName(value?.solo_accuracy, nameById);
  // Keyed by id as well, because two students in one session can share a
  // display name and the name map collapses them onto one pill.
  const byId = {};
  (value?.solo_accuracy || []).forEach((s) => {
    if (s?.student_id) byId[s.student_id] = s;
  });

  const code = focus.code || fallbackCode(value);
  // The roster used to come from the model's report while the scores came from
  // the session. merged_session_report_v11 writes no mathLearning.category, so
  // the roster went empty and took the real scores down with it — the
  // fractions were sitting on the row the whole time. When the report names
  // nobody, the students the scores name are the roster.
  const namedByScores = (value?.solo_accuracy || [])
    .filter((s) => !s?.not_applicable && !s?.notApplicable)
    .map((s) => {
      const name =
        nameById[s.student_id] || s.pseudonym || s.student_name || s.student_id;
      return name ? { student_name: name, student_id: s.student_id } : null;
    })
    .filter(Boolean);
  const reported = value?.student_math_levels || [];
  const students = reported.length > 0 ? reported : namedByScores;
  const participants = value?.participant_names || [];
  // An em dash says the student attempted nothing. We can only say that when we
  // actually have an answer: an empty list is one ("nobody attempted"), a
  // missing field is not — a server that predates the field has told us
  // nothing, and the row keeps the level badge it has always shown. Same for
  // before the names that match scores to rows have arrived.
  const namesResolved = Object.keys(nameById).length > 0;
  const showScores = Array.isArray(value?.solo_accuracy) && (namesResolved || reported.length === 0);
  const isScoresUnavailable = value?.solo_accuracy_status === "scores_unavailable";
  const pillFor = (student) =>
    (student.student_id && byId[student.student_id]) ||
    byName[String(student.student_name || "").toLowerCase()];

  return (
    <>
      <SectionLabel>
        Math Scores for {code && (
          <CodeText title={focus.codeTooltip || undefined}>{code}</CodeText>
        )}
        {focus.countLabel && (
          <>
            <Separator>{code ? " · " : null}</Separator>
            <CountLabel>{focus.countLabel}</CountLabel>
          </>
        )}
        {focus.title && (
          <>
            <Separator>{code || focus.countLabel ? ": " : null}</Separator>
            <TitleText>{focus.title}</TitleText>
          </>
        )}
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`solo-accuracy-session-tooltip-${value?.session_id || "scores"}`}>
              {SOLO_ACCURACY_RULE}
            </Tooltip>
          }
        >
          <InlineInfoIcon src={info2} alt="Info" />
        </OverlayTrigger>
      </SectionLabel>
      {isScoresUnavailable ? (
        <PillMeta>Scores unavailable</PillMeta>
      ) : students.length > 0 ? (
        <ScoredStudents students={students} pillFor={pillFor} showScores={showScores} />
      ) : participants.length > 0 ? (
        <ParticipantNames names={participants} />
      ) : (
        <PillMeta>N/A</PillMeta>
      )}
    </>
  );
};
